/**
 * Settings API Layer.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import type { OrgConfig } from "@/types/settings";

export function useOrgConfig() {
  return useQuery<OrgConfig>({
    queryKey: ["org-config"],
    queryFn: async () =>
      (await apiClient.get("/api/v1/settings/config")).data as OrgConfig,
    staleTime: 5 * 60 * 1000, // 5 minutes — config rarely changes.
  });
}

export function useUpdateSection() {
  const qc = useQueryClient();
  return useMutation<
    OrgConfig,
    Error,
    { section: string; data: Record<string, unknown> }
  >({
    mutationFn: async ({ section, data }) =>
      (await apiClient.put(`/api/v1/settings/sections/${section}`, { data }))
        .data as OrgConfig,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["org-config"] }),
  });
}

export function useResetSection() {
  const qc = useQueryClient();
  return useMutation<OrgConfig, Error, string>({
    mutationFn: async (section) =>
      (await apiClient.delete(`/api/v1/settings/sections/${section}`))
        .data as OrgConfig,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["org-config"] }),
  });
}
